export type SupplierAutoOrderChannel = 'sms' | 'phone' | 'platform' | 'manual';

export type SupplierAutoPaymentMode =
  | 'postpaid'
  | 'bank_transfer'
  | 'manual_online'
  | 'platform_required'
  | 'order_only';

export type SupplierAutoConnectorStatus = 'manual' | 'connector_planned' | 'api_planned';

export type SupplierAutoApprovalMode = 'manager_required' | 'manager_optional';

export type SupplierAutoSettlementCycle = 'per_order' | 'weekly' | 'monthly' | 'platform';

export type SupplierAutoStatus = 'active' | 'paused';

export type SupplierAutoDraftStatus = 'candidate' | 'approved' | 'held';

export type SupplierAutoValidationSeverity = 'error' | 'warning';

export type SupplierAutoValidationStage = 'supplier_master' | 'payment_policy' | 'supply_items';

export const SUPPLIER_AUTO_PERSISTENCE_VERSION = 1;

export interface SupplierAutoBusinessProfile {
  businessName: string;
  businessNumber: string;
  mainPhone: string;
  address: string;
  orderCutoffTime: string;
  defaultDeliveryMemo: string;
}

export interface SupplierAutoSettlementPolicy {
  settlementCycle: SupplierAutoSettlementCycle;
  bankName: string;
  bankAccount: string;
  accountHolder: string;
  taxInvoiceEmail: string;
}

export interface SupplierAutoPaymentPolicy {
  mode: SupplierAutoPaymentMode;
  connectorStatus: SupplierAutoConnectorStatus;
  approvalMode: SupplierAutoApprovalMode;
  settlementPolicy: SupplierAutoSettlementPolicy;
  memo: string;
}

export interface SupplierAutoExternalMarketProfile {
  marketName: string;
  accountLabel: string;
  orderPageUrl: string;
  lastVerifiedAt: string;
  automationMemo: string;
}

export interface SupplierAutoSupplyItem {
  id: string;
  materialId: string;
  supplierSku: string;
  orderUnit: string;
  packSize: number;
  minOrderQuantity: number;
  unitPrice: number;
  leadTimeDays: number;
  autoOrderEnabled: boolean;
  memo: string;
}

export interface SupplierAutoSupplier {
  id: string;
  name: string;
  ownerName: string;
  smsRecipient: string;
  businessProfile: SupplierAutoBusinessProfile;
  status: SupplierAutoStatus;
  orderChannel: SupplierAutoOrderChannel;
  paymentPolicy: SupplierAutoPaymentPolicy;
  externalMarketProfile: SupplierAutoExternalMarketProfile;
  orderMemo: string;
  supplyItems: SupplierAutoSupplyItem[];
}

export interface SupplierAutoValidationIssue {
  id: string;
  supplierId: string;
  supplyItemId?: string;
  severity: SupplierAutoValidationSeverity;
  stage: SupplierAutoValidationStage;
  title: string;
  detail: string;
}

export interface SupplierAutoMaterial {
  id: string;
  name: string;
  category: string;
  locationName: string;
  currentStock: number;
  minStock: number;
  targetStock: number;
  comparisonUnit: string;
  orderUnit: string;
  lastInboundAt: string;
}

export interface SupplierAutoDraftLine {
  id: string;
  materialId: string;
  supplyItemId: string;
  materialName: string;
  category: string;
  locationName: string;
  currentStock: number;
  minStock: number;
  targetStock: number;
  shortageQuantity: number;
  suggestedQuantity: number;
  orderQuantity: number;
  orderUnit: string;
  unitPrice: number;
  amount: number;
  excluded: boolean;
  reason: string;
}

export interface SupplierAutoDraft {
  id: string;
  supplierId: string;
  supplierName: string;
  status: SupplierAutoDraftStatus;
  createdAt: string;
  paymentPolicy: SupplierAutoPaymentPolicy;
  orderChannel: SupplierAutoOrderChannel;
  totalAmount: number;
  warnings: string[];
  lines: SupplierAutoDraftLine[];
  phonePreview: string;
}

export interface SupplierAutoHistoryEntry {
  id: string;
  at: string;
  actor: string;
  action: string;
  detail: string;
  draftId?: string;
}

export interface SupplierAutoState {
  suppliers: SupplierAutoSupplier[];
  materials: SupplierAutoMaterial[];
  drafts: SupplierAutoDraft[];
  selectedSupplierId: string;
  activeDraftId: string | null;
  history: SupplierAutoHistoryEntry[];
}

export interface SupplierAutoPersistenceSnapshot {
  version: typeof SUPPLIER_AUTO_PERSISTENCE_VERSION;
  savedAt: string;
  suppliers: SupplierAutoSupplier[];
  materials?: SupplierAutoMaterial[];
  sosSupplierRecords?: SupplierAutoSosSupplierRecord[];
}

export interface SupplierAutoPersistenceAdapter {
  load(): SupplierAutoPersistenceSnapshot | Promise<SupplierAutoPersistenceSnapshot>;
  save(snapshot: SupplierAutoPersistenceSnapshot): SupplierAutoPersistenceSnapshot | Promise<SupplierAutoPersistenceSnapshot>;
}

export interface SupplierAutoSaveCheck {
  canSave: boolean;
  blockingIssueCount: number;
  warningIssueCount: number;
  issues: SupplierAutoValidationIssue[];
}

export const SUPPLIER_AUTO_METADATA_KEY = 'supplierAuto';

export type SupplierAutoSosSupplierStatus = 'active' | 'inactive';

export interface SupplierAutoSosSupplierRecord {
  id: string;
  name: string;
  phone?: string | null;
  address?: string | null;
  contactName?: string | null;
  monthlyVolumeWon?: number | string | null;
  autoOrderEnabled?: boolean | null;
  defaultLeadTimeDays?: number | null;
  smsRecipient?: string | null;
  status?: SupplierAutoSosSupplierStatus | string | null;
  metadata?: Record<string, unknown> | null;
}

export interface SupplierAutoSosSupplierMetadata {
  source: 'supplier-auto';
  version: typeof SUPPLIER_AUTO_PERSISTENCE_VERSION;
  supplierAutoId: string;
  supplierName: string;
  status: SupplierAutoStatus;
  orderChannel: SupplierAutoOrderChannel;
  businessProfile: SupplierAutoBusinessProfile;
  paymentPolicy: SupplierAutoPaymentPolicy;
  externalMarketProfile: SupplierAutoExternalMarketProfile;
  orderMemo: string;
  supplyItems: SupplierAutoSupplyItem[];
  validation: {
    blockingIssueCount: number;
    warningIssueCount: number;
    issues: Array<Pick<SupplierAutoValidationIssue, 'severity' | 'stage' | 'title' | 'detail' | 'supplyItemId'>>;
  };
  savedAt: string;
}

export interface SupplierAutoSosSupplierInput {
  name: string;
  phone?: string;
  address?: string;
  contactName?: string;
  monthlyVolumeWon?: number;
  autoOrderEnabled: boolean;
  defaultLeadTimeDays: number;
  smsRecipient?: string;
  status?: SupplierAutoSosSupplierStatus;
  metadata: Record<string, unknown>;
}

export interface SupplierAutoSosSupplierUpsert {
  supplierAutoId: string;
  sosSupplierId: string | null;
  operation: 'create' | 'update';
  endpoint: 'POST /v1/suppliers' | 'PATCH /v1/suppliers/:id';
  input: SupplierAutoSosSupplierInput;
}

export interface SupplierAutoSosAdapterReport {
  ready: boolean;
  sourceTable: 'suppliers';
  metadataKey: typeof SUPPLIER_AUTO_METADATA_KEY;
  supplierCount: number;
  upsertCount: number;
  createCount: number;
  updateCount: number;
  blockingIssueCount: number;
  warningIssueCount: number;
}

export interface SupplierAutoSosAdapterPayload {
  report: SupplierAutoSosAdapterReport;
  upserts: SupplierAutoSosSupplierUpsert[];
}

export interface SupplierAutoSosAdapterOptions {
  existingSuppliers?: SupplierAutoSosSupplierRecord[];
  savedAt?: string;
}

export type CoupangMcpScenarioStatus = 'ready' | 'review_required' | 'blocked';

export type CoupangMcpStageStatus = 'ready' | 'review_required' | 'blocked';

export type CoupangMcpStageOwner = 'app' | 'core' | 'agent' | 'external_user';

export type CoupangMcpStageId =
  | 'draft_intake'
  | 'platform_scope'
  | 'material_mapping'
  | 'quantity_contract'
  | 'cart_audit'
  | 'checkout_handoff'
  | 'payment_handoff'
  | 'soldout_fallback'
  | 'result_review';

export interface CoupangMcpDraftLine {
  id: string;
  materialId: string;
  supplyItemId: string;
  materialName: string;
  supplierSku: string;
  orderQuantity: number;
  orderUnit: string;
  amount: number;
}

export interface CoupangMcpStage {
  id: CoupangMcpStageId;
  label: string;
  status: CoupangMcpStageStatus;
  owner: CoupangMcpStageOwner;
  source: string;
  codeApi: string;
  evidence: string;
  detail: string;
}

export interface CoupangMcpScenarioPayload {
  sourceContract: {
    draftId: string | null;
    supplierId: string | null;
    quantityField: 'SupplierAutoDraftLine.orderQuantity';
    quantityRule: string;
    platformScope: 'coupang_only';
  };
  supplier: {
    name: string;
    orderChannel: SupplierAutoOrderChannel;
    paymentMode: SupplierAutoPaymentMode;
    connectorStatus: SupplierAutoConnectorStatus;
    marketName: string;
    orderPageUrl: string;
  } | null;
  draft: {
    id: string;
    status: SupplierAutoDraftStatus;
    totalAmount: number;
  } | null;
  lines: CoupangMcpDraftLine[];
  soldOutFallback: {
    policy: string;
    rocketDeliveryPriority: boolean;
    multipleCandidateRule: string;
  };
  approvalBoundaries: string[];
}

export interface CoupangMcpScenario {
  status: CoupangMcpScenarioStatus;
  title: string;
  summary: string;
  supplierName: string;
  platformName: string;
  activeLineCount: number;
  blockers: string[];
  reviewItems: string[];
  lines: CoupangMcpDraftLine[];
  stages: CoupangMcpStage[];
  payload: CoupangMcpScenarioPayload;
}

export const paymentModeLabels: Record<SupplierAutoPaymentMode, string> = {
  postpaid: '후불 정산',
  bank_transfer: '무통장 입금',
  manual_online: '수동 온라인 결제',
  platform_required: '플랫폼 결제 필요',
  order_only: '결제 없이 주문서만',
};

export const orderChannelLabels: Record<SupplierAutoOrderChannel, string> = {
  sms: '문자 주문',
  phone: '전화 주문',
  platform: '플랫폼 주문',
  manual: '수동 처리',
};

export const connectorStatusLabels: Record<SupplierAutoConnectorStatus, string> = {
  manual: '수동 확인',
  connector_planned: '커넥터 예정',
  api_planned: 'API 예정',
};

export const approvalModeLabels: Record<SupplierAutoApprovalMode, string> = {
  manager_required: '관리자 승인 필수',
  manager_optional: '관리자 승인 선택',
};

export const settlementCycleLabels: Record<SupplierAutoSettlementCycle, string> = {
  per_order: '건별 정산',
  weekly: '주간 정산',
  monthly: '월말 정산',
  platform: '플랫폼 정산',
};

const DEMO_CREATED_AT = '2026-05-24T09:00:00.000+09:00';

export function createInitialSupplierAutoState(): SupplierAutoState {
  const suppliers = createDemoSuppliers();
  const materials = createDemoMaterials();
  const drafts = buildAutoOrderDrafts(suppliers, materials, DEMO_CREATED_AT);

  return {
    suppliers,
    materials,
    drafts,
    selectedSupplierId: suppliers[0]?.id ?? '',
    activeDraftId: drafts[0]?.id ?? null,
    history: [
      {
        id: 'history-ready',
        at: DEMO_CREATED_AT,
        actor: 'system',
        action: '시뮬레이션 준비',
        detail: 'mock 상태에서 거래처별 발주 후보를 계산했습니다.',
      },
    ],
  };
}

export function buildAutoOrderDrafts(
  suppliers: SupplierAutoSupplier[],
  materials: SupplierAutoMaterial[],
  createdAt = new Date().toISOString(),
): SupplierAutoDraft[] {
  const materialsById = new Map(materials.map((material) => [material.id, material]));

  return suppliers.flatMap((supplier) => {
    if (supplier.status !== 'active') {
      return [];
    }

    const lines = supplier.supplyItems.flatMap((supplyItem) => {
      if (!supplyItem.autoOrderEnabled) {
        return [];
      }

      const material = materialsById.get(supplyItem.materialId);
      if (!material) {
        return [];
      }

      const line = createDraftLine(supplier, supplyItem, material);
      return line ? [line] : [];
    });

    if (lines.length === 0) {
      return [];
    }

    return [finalizeDraft({
      id: `draft-${supplier.id}`,
      supplierId: supplier.id,
      supplierName: supplier.name,
      status: 'candidate',
      createdAt,
      paymentPolicy: supplier.paymentPolicy,
      orderChannel: supplier.orderChannel,
      totalAmount: 0,
      warnings: createDraftWarnings(supplier),
      lines,
      phonePreview: '',
    }, supplier)];
  });
}

export function setSelectedSupplier(state: SupplierAutoState, supplierId: string): SupplierAutoState {
  const supplierExists = state.suppliers.some((supplier) => supplier.id === supplierId);
  if (!supplierExists) {
    return state;
  }

  const activeDraftId = state.drafts.find((draft) => draft.supplierId === supplierId)?.id ?? state.activeDraftId;
  return {
    ...state,
    selectedSupplierId: supplierId,
    activeDraftId,
  };
}

export function patchSupplier(
  state: SupplierAutoState,
  supplierId: string,
  patch: Partial<Pick<SupplierAutoSupplier, 'name' | 'ownerName' | 'smsRecipient' | 'status' | 'orderChannel' | 'orderMemo'>>,
): SupplierAutoState {
  return rebuildStateDrafts({
    ...state,
    suppliers: state.suppliers.map((supplier) => (
      supplier.id === supplierId ? { ...supplier, ...patch } : supplier
    )),
  });
}

export function addSupplier(state: SupplierAutoState): SupplierAutoState {
  const supplier = createBlankSupplier(state.suppliers.length + 1);

  return rebuildStateDrafts({
    ...state,
    suppliers: [supplier, ...state.suppliers],
    selectedSupplierId: supplier.id,
    activeDraftId: null,
    history: [
      createHistoryEntry('manager', '거래처 추가', `${supplier.name} 기본 정보를 추가했습니다.`),
      ...state.history,
    ],
  });
}

export function patchSupplierBusinessProfile(
  state: SupplierAutoState,
  supplierId: string,
  patch: Partial<SupplierAutoBusinessProfile>,
): SupplierAutoState {
  return rebuildStateDrafts({
    ...state,
    suppliers: state.suppliers.map((supplier) => (
      supplier.id === supplierId
        ? { ...supplier, businessProfile: { ...supplier.businessProfile, ...patch } }
        : supplier
    )),
  });
}

export function patchSupplierPaymentPolicy(
  state: SupplierAutoState,
  supplierId: string,
  patch: Partial<SupplierAutoPaymentPolicy>,
): SupplierAutoState {
  return rebuildStateDrafts({
    ...state,
    suppliers: state.suppliers.map((supplier) => (
      supplier.id === supplierId
        ? { ...supplier, paymentPolicy: { ...supplier.paymentPolicy, ...patch } }
        : supplier
    )),
  });
}

export function patchSupplierSettlementPolicy(
  state: SupplierAutoState,
  supplierId: string,
  patch: Partial<SupplierAutoSettlementPolicy>,
): SupplierAutoState {
  return rebuildStateDrafts({
    ...state,
    suppliers: state.suppliers.map((supplier) => (
      supplier.id === supplierId
        ? {
          ...supplier,
          paymentPolicy: {
            ...supplier.paymentPolicy,
            settlementPolicy: {
              ...supplier.paymentPolicy.settlementPolicy,
              ...patch,
            },
          },
        }
        : supplier
    )),
  });
}

export function patchSupplierExternalMarketProfile(
  state: SupplierAutoState,
  supplierId: string,
  patch: Partial<SupplierAutoExternalMarketProfile>,
): SupplierAutoState {
  return rebuildStateDrafts({
    ...state,
    suppliers: state.suppliers.map((supplier) => (
      supplier.id === supplierId
        ? { ...supplier, externalMarketProfile: { ...supplier.externalMarketProfile, ...patch } }
        : supplier
    )),
  });
}

export function createSupplierAutoPersistenceSnapshot(
  state: SupplierAutoState,
  savedAt = new Date().toISOString(),
): SupplierAutoPersistenceSnapshot {
  return {
    version: SUPPLIER_AUTO_PERSISTENCE_VERSION,
    savedAt,
    suppliers: cloneSuppliers(state.suppliers),
    materials: cloneMaterials(state.materials),
  };
}

export function applySupplierAutoPersistenceSnapshot(
  state: SupplierAutoState,
  snapshot: SupplierAutoPersistenceSnapshot,
): SupplierAutoState {
  return rebuildStateDrafts({
    ...state,
    suppliers: cloneSuppliers(snapshot.suppliers),
    materials: snapshot.materials ? cloneMaterials(snapshot.materials) : state.materials,
  });
}

export function createSupplierAutoPersistenceFingerprint(
  stateOrSnapshot: SupplierAutoState | SupplierAutoPersistenceSnapshot,
): string {
  const suppliers = 'suppliers' in stateOrSnapshot ? stateOrSnapshot.suppliers : [];
  return JSON.stringify(cloneSuppliers(suppliers));
}

export function getSupplierAutoSaveCheck(state: SupplierAutoState): SupplierAutoSaveCheck {
  const issues = state.suppliers.flatMap((supplier) => validateSupplierSetup(supplier));
  const blockingIssueCount = issues.filter((issue) => issue.severity === 'error').length;
  const warningIssueCount = issues.filter((issue) => issue.severity === 'warning').length;

  return {
    canSave: blockingIssueCount === 0,
    blockingIssueCount,
    warningIssueCount,
    issues,
  };
}

export function createSupplierAutoSosAdapterPayload(
  state: SupplierAutoState,
  options: SupplierAutoSosAdapterOptions = {},
): SupplierAutoSosAdapterPayload {
  return createSupplierAutoSosAdapterPayloadFromSuppliers(state.suppliers, options);
}

export function createSupplierAutoSosAdapterPayloadFromSuppliers(
  suppliers: SupplierAutoSupplier[],
  options: SupplierAutoSosAdapterOptions = {},
): SupplierAutoSosAdapterPayload {
  const issues = suppliers.flatMap((supplier) => validateSupplierSetup(supplier));
  const blockingIssueCount = issues.filter((issue) => issue.severity === 'error').length;
  const warningIssueCount = issues.filter((issue) => issue.severity === 'warning').length;
  const savedAt = options.savedAt ?? new Date().toISOString();
  const upserts = suppliers.map((supplier) => createSupplierAutoSosSupplierUpsert(supplier, {
    existingSuppliers: options.existingSuppliers,
    savedAt,
  }));
  const createCount = upserts.filter((upsert) => upsert.operation === 'create').length;
  const updateCount = upserts.length - createCount;

  return {
    report: {
      ready: blockingIssueCount === 0,
      sourceTable: 'suppliers',
      metadataKey: SUPPLIER_AUTO_METADATA_KEY,
      supplierCount: suppliers.length,
      upsertCount: upserts.length,
      createCount,
      updateCount,
      blockingIssueCount,
      warningIssueCount,
    },
    upserts,
  };
}

export function buildCoupangMcpScenario(
  draft: SupplierAutoDraft | null,
  supplier: SupplierAutoSupplier | null = null,
): CoupangMcpScenario {
  const lines = draft && supplier
    ? createCoupangMcpLines(draft, supplier)
    : [];
  const isPlatformOrder = Boolean(
    supplier && (
      supplier.orderChannel === 'platform'
      || supplier.paymentPolicy.mode === 'platform_required'
      || supplier.paymentPolicy.mode === 'manual_online'
    ),
  );
  const isCoupangPlatform = Boolean(supplier && isCoupangSupplierContext(supplier, draft));
  const platformName = supplier?.externalMarketProfile.marketName.trim()
    || (isCoupangPlatform ? '쿠팡' : '-');
  const supplierName = supplier?.name || draft?.supplierName || '-';

  const stages: CoupangMcpStage[] = [
    {
      id: 'draft_intake',
      label: '처리할 발주서 입력',
      status: draft && lines.length > 0 ? 'ready' : 'blocked',
      owner: 'app',
      source: 'SupplierAutoDraft.lines / production orders + order_lines',
      codeApi: 'SupplierAutoDraft, SupplierAutoDraftLine',
      evidence: draft ? `${lines.length}개 활성 발주 품목` : '선택된 발주 후보 없음',
      detail: '발주 품목과 수량은 이미 발주서 입력 계약에서 넘어온 값만 사용합니다.',
    },
    {
      id: 'platform_scope',
      label: '쿠팡 플랫폼 범위',
      status: supplier && isPlatformOrder && isCoupangPlatform ? 'ready' : 'blocked',
      owner: 'core',
      source: 'SupplierAutoSupplier.orderChannel + externalMarketProfile',
      codeApi: 'SupplierAutoSupplier.paymentPolicy, SupplierAutoExternalMarketProfile',
      evidence: supplier
        ? `${supplier.orderChannel} / ${platformName} / ${supplier.externalMarketProfile.orderPageUrl || '-'}`
        : '선택된 거래처 없음',
      detail: '이 시나리오는 쿠팡 전용 어댑터입니다. 네이버나 일반몰은 별도 어댑터 증거가 필요합니다.',
    },
    {
      id: 'material_mapping',
      label: '재료-쿠팡 상품 매핑',
      status: lines.length > 0 ? 'review_required' : 'blocked',
      owner: 'core',
      source: 'supplier supplyItems + Coupang product/order evidence',
      codeApi: 'SupplierAutoSupplyItem.supplierSku',
      evidence: lines.length > 0
        ? 'zip 5: 사누끼 주문 완료, 한우대창 품절 매핑 증거'
        : '검토할 활성 품목 없음',
      detail: '내부 재료명은 검색어가 아닙니다. 과거 쿠팡 상품명/옵션 증거와 매핑을 검토해야 합니다.',
    },
    {
      id: 'quantity_contract',
      label: '수량 계약',
      status: lines.length > 0 ? 'ready' : 'blocked',
      owner: 'core',
      source: 'SupplierAutoDraftLine.orderQuantity',
      codeApi: 'SupplierAutoDraftLine.orderQuantity',
      evidence: lines.length > 0
        ? lines.map((line) => `${line.materialName} ${line.orderQuantity}${line.orderUnit}`).join(', ')
        : '수량 없음',
      detail: '발주 수량을 다시 환산하지 않습니다. 쿠팡 옵션 묶음이 필요할 때만 장바구니 옵션 조합을 풉니다.',
    },
    {
      id: 'cart_audit',
      label: '장바구니 대상 품목 검수',
      status: isCoupangPlatform && lines.length > 0 ? 'review_required' : 'blocked',
      owner: 'agent',
      source: 'Coupang cart browser evidence',
      codeApi: 'future Coupang browser adapter',
      evidence: 'zip 5: 선택 대상만 체크된 장바구니 증거',
      detail: '기존 장바구니의 비대상 품목은 선택 해제하고 이번 발주 대상만 검토해야 합니다.',
    },
    {
      id: 'checkout_handoff',
      label: '외부 결제 화면 전달',
      status: isCoupangPlatform && lines.length > 0 ? 'review_required' : 'blocked',
      owner: 'agent',
      source: 'Coupang checkout browser evidence',
      codeApi: 'future Coupang checkout adapter',
      evidence: 'zip 5: 결제 화면과 주문 완료 증거',
      detail: '결제는 앱 안에서 하지 않습니다. 쿠팡 결제 화면으로 넘기고 증거를 회수합니다.',
    },
    {
      id: 'payment_handoff',
      label: '사용자 결제 승인',
      status: isCoupangPlatform ? 'review_required' : 'blocked',
      owner: 'external_user',
      source: 'external Coupang payment screen',
      codeApi: 'no DVVB checkout, no payment API',
      evidence: 'zip 5: 쿠팡 결제 후 주문번호 회수 증거',
      detail: '최종 결제 액션은 외부 플랫폼에서 사용자가 수행합니다. 현재 1차는 자동 결제 실행을 포함하지 않습니다.',
    },
    {
      id: 'soldout_fallback',
      label: '품절 대체 검색',
      status: isCoupangPlatform ? 'ready' : 'blocked',
      owner: 'agent',
      source: 'Coupang search result evidence',
      codeApi: 'future Coupang search adapter',
      evidence: 'zip 7: 품절 시 재검색, 로켓 우선, 복수 후보 확인 필요',
      detail: '품절/상품 없음이면 재검색하고 로켓배송을 우선합니다. 후보가 여러 개면 사용자 확인 전 장바구니에 넣지 않습니다.',
    },
    {
      id: 'result_review',
      label: '결과 검토와 기록',
      status: draft ? 'ready' : 'blocked',
      owner: 'app',
      source: 'Supplier-auto result panel',
      codeApi: 'CoupangMcpScenarioPayload',
      evidence: 'structured scenario payload',
      detail: '주문번호, 품절/후보, 결제 후 증거를 결과로 보여줍니다. 발주서 상태 writeback은 별도 승인 전 실행하지 않습니다.',
    },
  ];
  const blockers = stages
    .filter((stage) => stage.status === 'blocked')
    .map((stage) => `${stage.label}: ${stage.detail}`);
  const reviewItems = stages
    .filter((stage) => stage.status === 'review_required')
    .map((stage) => `${stage.label}: ${stage.detail}`);
  const status: CoupangMcpScenarioStatus = blockers.length > 0
    ? 'blocked'
    : reviewItems.length > 0
      ? 'review_required'
      : 'ready';

  return {
    status,
    title: '쿠팡 MCP 어댑터 시나리오',
    summary: status === 'blocked'
      ? '쿠팡 플랫폼 발주 후보가 아니어서 어댑터 실행 조건이 막혔습니다.'
      : '쿠팡 전용 장바구니/결제 하네스가 읽을 수 있는 1차 시나리오 계약입니다.',
    supplierName,
    platformName,
    activeLineCount: lines.length,
    blockers,
    reviewItems,
    lines,
    stages,
    payload: {
      sourceContract: {
        draftId: draft?.id ?? null,
        supplierId: supplier?.id ?? null,
        quantityField: 'SupplierAutoDraftLine.orderQuantity',
        quantityRule: '발주서 수량을 다시 환산하지 않고 쿠팡 옵션 조합만 풉니다.',
        platformScope: 'coupang_only',
      },
      supplier: supplier
        ? {
          name: supplier.name,
          orderChannel: supplier.orderChannel,
          paymentMode: supplier.paymentPolicy.mode,
          connectorStatus: supplier.paymentPolicy.connectorStatus,
          marketName: supplier.externalMarketProfile.marketName,
          orderPageUrl: supplier.externalMarketProfile.orderPageUrl,
        }
        : null,
      draft: draft
        ? {
          id: draft.id,
          status: draft.status,
          totalAmount: draft.totalAmount,
        }
        : null,
      lines,
      soldOutFallback: {
        policy: 'mapped product sold out or missing -> search again -> prefer Rocket delivery -> require confirmation for multiple candidates',
        rocketDeliveryPriority: true,
        multipleCandidateRule: 'do not add replacement to cart until the user confirms one candidate',
      },
      approvalBoundaries: [
        'No production API/token call in this harness.',
        'No DB/schema/order-status writeback without exact approval.',
        'No automatic payment or cancellation execution in the current semi-auto path.',
      ],
    },
  };
}

export function createSupplierAutoSosSupplierUpsert(
  supplier: SupplierAutoSupplier,
  options: SupplierAutoSosAdapterOptions = {},
): SupplierAutoSosSupplierUpsert {
  const existingSupplier = findExistingSosSupplier(supplier, options.existingSuppliers ?? []);
  const input = createSupplierAutoSosSupplierInput(
    supplier,
    existingSupplier?.metadata,
    options.savedAt ?? new Date().toISOString(),
  );
  const operation = existingSupplier ? 'update' : 'create';

  if (operation === 'update') {
    input.status = mapSupplierAutoStatusToSosStatus(supplier.status);
  }

  return {
    supplierAutoId: supplier.id,
    sosSupplierId: existingSupplier?.id ?? null,
    operation,
    endpoint: operation === 'update' ? 'PATCH /v1/suppliers/:id' : 'POST /v1/suppliers',
    input,
  };
}

export function createSupplierAutoSosSupplierInput(
  supplier: SupplierAutoSupplier,
  existingMetadata: Record<string, unknown> | null | undefined = undefined,
  savedAt = new Date().toISOString(),
): SupplierAutoSosSupplierInput {
  const input: SupplierAutoSosSupplierInput = {
    name: supplier.name.trim(),
    autoOrderEnabled: supplier.status === 'active' && supplier.supplyItems.some((item) => item.autoOrderEnabled),
    defaultLeadTimeDays: calculateDefaultLeadTimeDays(supplier),
    metadata: createSupplierAutoSosMetadata(supplier, existingMetadata, savedAt),
  };

  assignOptionalText(input, 'phone', supplier.businessProfile.mainPhone);
  assignOptionalText(input, 'address', supplier.businessProfile.address);
  assignOptionalText(input, 'contactName', supplier.ownerName);
  assignOptionalText(input, 'smsRecipient', supplier.smsRecipient);

  return input;
}

export function createSupplierAutoPersistenceSnapshotFromSosSuppliers(
  records: SupplierAutoSosSupplierRecord[],
  materials: SupplierAutoMaterial[] = [],
  savedAt = new Date().toISOString(),
): SupplierAutoPersistenceSnapshot {
  return {
    version: SUPPLIER_AUTO_PERSISTENCE_VERSION,
    savedAt,
    suppliers: records.map((record) => createSupplierAutoSupplierFromSosRecord(record)),
    materials: cloneMaterials(materials),
    sosSupplierRecords: records.map((record) => ({ ...record })),
  };
}

export function createSupplierAutoSupplierFromSosRecord(
  record: SupplierAutoSosSupplierRecord,
): SupplierAutoSupplier {
  const metadata = readSupplierAutoMetadata(record.metadata);
  const businessProfile = readBusinessProfile(metadata?.businessProfile, record);
  const paymentPolicy = readPaymentPolicy(metadata?.paymentPolicy);
  const externalMarketProfile = readExternalMarketProfile(metadata?.externalMarketProfile);
  const supplyItems = readSupplyItems(metadata?.supplyItems);
  const metadataStatus = readString(metadata?.status);

  return {
    id: readString(metadata?.supplierAutoId) || record.id,
    name: record.name || readString(metadata?.supplierName) || '거래처',
    ownerName: record.contactName ?? '',
    smsRecipient: record.smsRecipient ?? '',
    businessProfile,
    status: record.status === 'inactive' || metadataStatus === 'paused' ? 'paused' : 'active',
    orderChannel: readOrderChannel(metadata?.orderChannel, record),
    paymentPolicy,
    externalMarketProfile,
    orderMemo: readString(metadata?.orderMemo),
    supplyItems,
  };
}

export function patchSupplyItem(
  state: SupplierAutoState,
  supplierId: string,
  supplyItemId: string,
  patch: Partial<Omit<SupplierAutoSupplyItem, 'id' | 'materialId'>>,
): SupplierAutoState {
  return rebuildStateDrafts({
    ...state,
    suppliers: state.suppliers.map((supplier) => {
      if (supplier.id !== supplierId) {
        return supplier;
      }

      return {
        ...supplier,
        supplyItems: supplier.supplyItems.map((supplyItem) => (
          supplyItem.id === supplyItemId ? { ...supplyItem, ...patch } : supplyItem
        )),
      };
    }),
  });
}

export function addSupplyItemMapping(
  state: SupplierAutoState,
  supplierId: string,
  materialId: string,
): SupplierAutoState {
  return addSupplyItemMappings(state, supplierId, [materialId]);
}

export function addSupplyItemMappings(
  state: SupplierAutoState,
  supplierId: string,
  materialIds: string[],
): SupplierAutoState {
  const uniqueMaterialIds = Array.from(new Set(materialIds));
  if (uniqueMaterialIds.length === 0) {
    return state;
  }

  const materialsById = new Map(state.materials.map((material) => [material.id, material]));
  const targetSupplier = state.suppliers.find((supplier) => supplier.id === supplierId);
  const existingMaterialIds = new Set(targetSupplier?.supplyItems.map((supplyItem) => supplyItem.materialId) ?? []);
  const nextItems = uniqueMaterialIds
    .filter((materialId) => !existingMaterialIds.has(materialId))
    .map((materialId) => materialsById.get(materialId))
    .filter((material): material is SupplierAutoMaterial => Boolean(material))
    .map((material) => createSupplyItemFromMaterial(supplierId, material));

  if (nextItems.length === 0) {
    return state;
  }

  const nextState = rebuildStateDrafts({
    ...state,
    suppliers: state.suppliers.map((supplier) => {
      if (supplier.id !== supplierId) {
        return supplier;
      }

      return {
        ...supplier,
        supplyItems: [...supplier.supplyItems, ...nextItems],
      };
    }),
  });

  const supplierName = targetSupplier?.name ?? '선택 거래처';

  return {
    ...nextState,
    history: [
      createHistoryEntry('manager', '공급물품 등록', `${supplierName}에 공급물품 ${nextItems.length}개를 등록했습니다.`),
      ...state.history,
    ],
  };
}

export function removeSupplyItemMapping(
  state: SupplierAutoState,
  supplierId: string,
  supplyItemId: string,
): SupplierAutoState {
  return rebuildStateDrafts({
    ...state,
    suppliers: state.suppliers.map((supplier) => (
      supplier.id === supplierId
        ? { ...supplier, supplyItems: supplier.supplyItems.filter((supplyItem) => supplyItem.id !== supplyItemId) }
        : supplier
    )),
  });
}

export function setDraftLineQuantity(
  state: SupplierAutoState,
  draftId: string,
  lineId: string,
  quantity: number,
): SupplierAutoState {
  return updateDraft(state, draftId, (draft, supplier) => {
    const lines = draft.lines.map((line) => {
      if (line.id !== lineId) {
        return line;
      }

      const orderQuantity = Math.max(0, quantity);
      return {
        ...line,
        orderQuantity,
        amount: roundMoney(orderQuantity * line.unitPrice),
      };
    });

    return finalizeDraft({ ...draft, lines }, supplier);
  });
}

export function setDraftLineExcluded(
  state: SupplierAutoState,
  draftId: string,
  lineId: string,
  excluded: boolean,
): SupplierAutoState {
  return updateDraft(state, draftId, (draft, supplier) => {
    const lines = draft.lines.map((line) => (
      line.id === lineId ? { ...line, excluded } : line
    ));

    return finalizeDraft({ ...draft, lines }, supplier);
  });
}

export function holdDraft(state: SupplierAutoState, draftId: string): SupplierAutoState {
  const draft = state.drafts.find((candidate) => candidate.id === draftId);
  if (!draft) {
    return state;
  }

  return {
    ...updateDraft(state, draftId, (current, supplier) => finalizeDraft({ ...current, status: 'held' }, supplier)),
    history: [
      createHistoryEntry('manager', '보류', `${draft.supplierName} 발주 후보를 보류했습니다.`, draft.id),
      ...state.history,
    ],
  };
}

export function approveDraft(state: SupplierAutoState, draftId: string): SupplierAutoState {
  const draft = state.drafts.find((candidate) => candidate.id === draftId);
  if (!draft) {
    return state;
  }

  const activeLineCount = draft.lines.filter((line) => !line.excluded && line.orderQuantity > 0).length;

  return {
    ...updateDraft(state, draftId, (current, supplier) => finalizeDraft({ ...current, status: 'approved' }, supplier)),
    history: [
      createHistoryEntry(
        'manager',
        '승인 시뮬레이션',
        `${draft.supplierName} 후보 ${activeLineCount}개 품목을 승인 상태로 표시했습니다.`,
        draft.id,
      ),
      ...state.history,
    ],
  };
}

export function rebuildStateDrafts(state: SupplierAutoState): SupplierAutoState {
  const drafts = buildAutoOrderDrafts(state.suppliers, state.materials);
  const selectedSupplierId = state.suppliers.some((supplier) => supplier.id === state.selectedSupplierId)
    ? state.selectedSupplierId
    : state.suppliers[0]?.id ?? '';
  const activeDraftId = drafts.some((draft) => draft.id === state.activeDraftId)
    ? state.activeDraftId
    : drafts.find((draft) => draft.supplierId === selectedSupplierId)?.id ?? drafts[0]?.id ?? null;

  return {
    ...state,
    drafts,
    selectedSupplierId,
    activeDraftId,
  };
}

export function validateSupplierSetup(supplier: SupplierAutoSupplier): SupplierAutoValidationIssue[] {
  const issues: SupplierAutoValidationIssue[] = [];

  const addIssue = (
    stage: SupplierAutoValidationStage,
    severity: SupplierAutoValidationSeverity,
    title: string,
    detail: string,
    supplyItemId?: string,
  ) => {
    issues.push({
      id: `${supplier.id}-${stage}-${supplyItemId ?? issues.length}`,
      supplierId: supplier.id,
      supplyItemId,
      severity,
      stage,
      title,
      detail,
    });
  };

  if (!supplier.name.trim()) {
    addIssue('supplier_master', 'error', '거래처명 필요', '자동발주 후보를 거래처별로 묶으려면 거래처명이 필요합니다.');
  }

  if (!supplier.ownerName.trim()) {
    addIssue('supplier_master', 'warning', '담당자 미입력', '발주 확인이나 전화 주문 시 담당자 정보가 비어 있습니다.');
  }

  if (!supplier.businessProfile.businessName.trim()) {
    addIssue('supplier_master', 'warning', '상호명 미입력', '문자/주문서에 표시할 거래처 상호명이 비어 있습니다.');
  }

  if (supplier.orderChannel === 'sms' && !supplier.smsRecipient.trim()) {
    addIssue('supplier_master', 'error', '문자 수신번호 필요', '문자 주문 거래처는 주문 수신번호가 필요합니다.');
  }

  if (supplier.smsRecipient.trim() && !/^\+?\d[\d\s-]{4,30}$/.test(supplier.smsRecipient.trim())) {
    addIssue('supplier_master', 'error', '문자 수신번호 형식 확인', '기존 SOS 거래처 API가 받을 수 있는 전화번호 형식이어야 합니다.');
  }

  if (supplier.orderChannel === 'platform' && supplier.paymentPolicy.connectorStatus === 'manual') {
    addIssue('payment_policy', 'warning', '플랫폼 수동 처리', '플랫폼 주문은 아직 실행 커넥터 없이 수동 확인 상태입니다.');
  }

  if (requiresExternalMarketProfile(supplier)) {
    if (!supplier.externalMarketProfile.marketName.trim()) {
      addIssue('payment_policy', 'error', '외부 마켓 필요', '온라인 결제/플랫폼 주문 거래처는 외부 마켓 이름이 필요합니다.');
    }

    if (!supplier.externalMarketProfile.orderPageUrl.trim()) {
      addIssue('payment_policy', 'error', '주문 페이지 필요', '결제 MCP가 접근할 외부 주문/결제 페이지 주소가 필요합니다.');
    }

    if (!supplier.externalMarketProfile.accountLabel.trim()) {
      addIssue('payment_policy', 'warning', '마켓 계정 확인 필요', '외부 마켓에 저장된 결제정보를 구분할 계정명이 필요합니다.');
    }

    if (
      supplier.paymentPolicy.connectorStatus !== 'manual'
      && supplier.paymentPolicy.approvalMode !== 'manager_required'
    ) {
      addIssue('payment_policy', 'warning', '자동결제 승인 정책 확인', '검증 전 결제 MCP는 관리자 승인 후 결제 단계로 넘어가야 합니다.');
    }
  }

  if (
    supplier.paymentPolicy.mode === 'bank_transfer'
    && (!supplier.paymentPolicy.settlementPolicy.bankName.trim()
      || !supplier.paymentPolicy.settlementPolicy.bankAccount.trim()
      || !supplier.paymentPolicy.settlementPolicy.accountHolder.trim())
  ) {
    addIssue('payment_policy', 'warning', '입금 계좌 확인 필요', '무통장 입금 거래처는 은행, 계좌번호, 예금주를 채워야 합니다.');
  }

  if (supplier.supplyItems.length === 0) {
    addIssue('supply_items', 'error', '공급물품 없음', '자동발주가 읽을 거래처별 공급물품 매핑이 없습니다.');
  }

  if (supplier.supplyItems.length > 0 && supplier.supplyItems.every((item) => !item.autoOrderEnabled)) {
    addIssue('supply_items', 'warning', '자동발주 품목 없음', '등록된 공급물품이 모두 자동발주 제외 상태입니다.');
  }

  const duplicatedMaterialIds = supplier.supplyItems
    .map((item) => item.materialId)
    .filter((materialId, index, list) => list.indexOf(materialId) !== index);
  if (duplicatedMaterialIds.length > 0) {
    addIssue('supply_items', 'error', '중복 공급물품', '같은 재료가 한 거래처에 두 번 이상 연결되어 있습니다.');
  }

  supplier.supplyItems.forEach((item) => {
    if (!item.supplierSku.trim()) {
      addIssue('supply_items', 'warning', '거래처 품목명 미입력', '공급물품별 거래처 품목명이 비어 있습니다.', item.id);
    }

    if (!item.orderUnit.trim()) {
      addIssue('supply_items', 'error', '발주 단위 필요', '공급물품의 발주 단위가 필요합니다.', item.id);
    }

    if (!Number.isFinite(item.packSize) || item.packSize <= 0) {
      addIssue('supply_items', 'warning', '팩 단위 확인 필요', '팩 단위가 0 이하이면 후보 수량을 묶음 단위로 올림 처리할 수 없습니다.', item.id);
    }

    if (!Number.isFinite(item.minOrderQuantity) || item.minOrderQuantity < 0) {
      addIssue('supply_items', 'warning', '최소 발주수량 확인 필요', '최소 발주수량은 0 이상이어야 합니다.', item.id);
    }

    if (!Number.isFinite(item.leadTimeDays) || item.leadTimeDays < 0) {
      addIssue('supply_items', 'warning', '리드타임 확인 필요', '입고 리드타임은 0일 이상이어야 합니다.', item.id);
    }
  });

  return issues;
}

export function formatSupplierAutoMoney(value: number): string {
  return new Intl.NumberFormat('ko-KR', {
    style: 'currency',
    currency: 'KRW',
    maximumFractionDigits: 0,
  }).format(value);
}

function createDraftLine(
  supplier: SupplierAutoSupplier,
  supplyItem: SupplierAutoSupplyItem,
  material: SupplierAutoMaterial,
): SupplierAutoDraftLine | null {
  const shortageQuantity = roundQuantity(material.targetStock - material.currentStock);

  if (material.currentStock > material.minStock || shortageQuantity <= 0) {
    return null;
  }

  const minimumQuantity = Math.max(shortageQuantity, supplyItem.minOrderQuantity);
  const suggestedQuantity = roundQuantity(roundUpToPack(minimumQuantity, supplyItem.packSize));
  const reason = material.currentStock <= 0
    ? '재고 0: 목표재고까지 보충'
    : `최소재고 ${material.minStock}${material.comparisonUnit} 이하`;

  return {
    id: `line-${supplier.id}-${material.id}`,
    materialId: material.id,
    supplyItemId: supplyItem.id,
    materialName: material.name,
    category: material.category,
    locationName: material.locationName,
    currentStock: material.currentStock,
    minStock: material.minStock,
    targetStock: material.targetStock,
    shortageQuantity,
    suggestedQuantity,
    orderQuantity: suggestedQuantity,
    orderUnit: supplyItem.orderUnit,
    unitPrice: supplyItem.unitPrice,
    amount: roundMoney(suggestedQuantity * supplyItem.unitPrice),
    excluded: false,
    reason,
  };
}

function createCoupangMcpLines(
  draft: SupplierAutoDraft,
  supplier: SupplierAutoSupplier,
): CoupangMcpDraftLine[] {
  return draft.lines
    .filter((line) => !line.excluded && line.orderQuantity > 0)
    .map((line) => {
      const supplyItem = supplier.supplyItems.find((item) => item.id === line.supplyItemId);

      return {
        id: line.id,
        materialId: line.materialId,
        supplyItemId: line.supplyItemId,
        materialName: line.materialName,
        supplierSku: supplyItem?.supplierSku ?? '',
        orderQuantity: line.orderQuantity,
        orderUnit: line.orderUnit,
        amount: line.amount,
      };
    });
}

function isCoupangSupplierContext(
  supplier: SupplierAutoSupplier,
  draft: SupplierAutoDraft | null,
): boolean {
  return [
    supplier.name,
    supplier.externalMarketProfile.marketName,
    supplier.externalMarketProfile.orderPageUrl,
    supplier.externalMarketProfile.accountLabel,
    supplier.externalMarketProfile.automationMemo,
    draft?.supplierName ?? '',
  ].some(isCoupangText);
}

function isCoupangText(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return normalized.includes('쿠팡') || normalized.includes('coupang');
}

function createSupplierAutoSosMetadata(
  supplier: SupplierAutoSupplier,
  existingMetadata: Record<string, unknown> | null | undefined,
  savedAt: string,
): Record<string, unknown> {
  const issues = validateSupplierSetup(supplier);
  const supplierAutoMetadata: SupplierAutoSosSupplierMetadata = {
    source: 'supplier-auto',
    version: SUPPLIER_AUTO_PERSISTENCE_VERSION,
    supplierAutoId: supplier.id,
    supplierName: supplier.name,
    status: supplier.status,
    orderChannel: supplier.orderChannel,
    businessProfile: { ...supplier.businessProfile },
    paymentPolicy: {
      ...supplier.paymentPolicy,
      settlementPolicy: { ...supplier.paymentPolicy.settlementPolicy },
    },
    externalMarketProfile: { ...supplier.externalMarketProfile },
    orderMemo: supplier.orderMemo,
    supplyItems: supplier.supplyItems.map((item) => ({ ...item })),
    validation: {
      blockingIssueCount: issues.filter((issue) => issue.severity === 'error').length,
      warningIssueCount: issues.filter((issue) => issue.severity === 'warning').length,
      issues: issues.map((issue) => ({
        severity: issue.severity,
        stage: issue.stage,
        title: issue.title,
        detail: issue.detail,
        supplyItemId: issue.supplyItemId,
      })),
    },
    savedAt,
  };

  return {
    ...(isRecord(existingMetadata) ? existingMetadata : {}),
    [SUPPLIER_AUTO_METADATA_KEY]: supplierAutoMetadata,
  };
}

function findExistingSosSupplier(
  supplier: SupplierAutoSupplier,
  existingSuppliers: SupplierAutoSosSupplierRecord[],
): SupplierAutoSosSupplierRecord | null {
  return existingSuppliers.find((record) => {
    if (record.id === supplier.id) {
      return true;
    }

    const metadata = readSupplierAutoMetadata(record.metadata);
    return metadata?.supplierAutoId === supplier.id;
  }) ?? null;
}

function readSupplierAutoMetadata(metadata: Record<string, unknown> | null | undefined): Record<string, unknown> | null {
  if (!isRecord(metadata)) {
    return null;
  }

  const supplierAutoMetadata = metadata[SUPPLIER_AUTO_METADATA_KEY];
  return isRecord(supplierAutoMetadata) ? supplierAutoMetadata : null;
}

function calculateDefaultLeadTimeDays(supplier: SupplierAutoSupplier): number {
  const leadTimeDays = supplier.supplyItems
    .filter((item) => item.autoOrderEnabled)
    .map((item) => Math.max(0, Math.round(item.leadTimeDays)))
    .filter((value) => Number.isFinite(value));

  return leadTimeDays.length > 0 ? Math.min(...leadTimeDays) : 1;
}

function mapSupplierAutoStatusToSosStatus(status: SupplierAutoStatus): SupplierAutoSosSupplierStatus {
  return status === 'active' ? 'active' : 'inactive';
}

function assignOptionalText<Key extends 'phone' | 'address' | 'contactName' | 'smsRecipient'>(
  input: SupplierAutoSosSupplierInput,
  key: Key,
  value: string,
) {
  const trimmed = value.trim();
  if (trimmed) {
    input[key] = trimmed;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readBusinessProfile(
  value: unknown,
  record: SupplierAutoSosSupplierRecord,
): SupplierAutoBusinessProfile {
  const profile = isRecord(value) ? value : {};
  return {
    businessName: readString(profile.businessName) || record.name || '',
    businessNumber: readString(profile.businessNumber),
    mainPhone: readString(profile.mainPhone) || (record.phone ?? ''),
    address: readString(profile.address) || (record.address ?? ''),
    orderCutoffTime: readString(profile.orderCutoffTime),
    defaultDeliveryMemo: readString(profile.defaultDeliveryMemo),
  };
}

function readPaymentPolicy(value: unknown): SupplierAutoPaymentPolicy {
  const policy = isRecord(value) ? value : {};
  return {
    mode: readPaymentMode(policy.mode),
    connectorStatus: readConnectorStatus(policy.connectorStatus),
    approvalMode: readApprovalMode(policy.approvalMode),
    settlementPolicy: readSettlementPolicy(policy.settlementPolicy),
    memo: readString(policy.memo),
  };
}

function readSettlementPolicy(value: unknown): SupplierAutoSettlementPolicy {
  const policy = isRecord(value) ? value : {};
  return {
    settlementCycle: readSettlementCycle(policy.settlementCycle),
    bankName: readString(policy.bankName),
    bankAccount: readString(policy.bankAccount),
    accountHolder: readString(policy.accountHolder),
    taxInvoiceEmail: readString(policy.taxInvoiceEmail),
  };
}

function readExternalMarketProfile(value: unknown): SupplierAutoExternalMarketProfile {
  const profile = isRecord(value) ? value : {};
  return {
    marketName: readString(profile.marketName),
    accountLabel: readString(profile.accountLabel),
    orderPageUrl: readString(profile.orderPageUrl),
    lastVerifiedAt: readString(profile.lastVerifiedAt),
    automationMemo: readString(profile.automationMemo),
  };
}

function readSupplyItems(value: unknown): SupplierAutoSupplyItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item, index) => {
      if (!isRecord(item)) {
        return null;
      }

      const materialId = readString(item.materialId);
      if (!materialId) {
        return null;
      }

      return {
        id: readString(item.id) || `supply-imported-${index}`,
        materialId,
        supplierSku: readString(item.supplierSku),
        orderUnit: readString(item.orderUnit) || 'ea',
        packSize: readNumber(item.packSize, 1),
        minOrderQuantity: readNumber(item.minOrderQuantity, 1),
        unitPrice: readNumber(item.unitPrice, 0),
        leadTimeDays: readNumber(item.leadTimeDays, 1),
        autoOrderEnabled: item.autoOrderEnabled !== false,
        memo: readString(item.memo),
      };
    })
    .filter((item): item is SupplierAutoSupplyItem => Boolean(item));
}

function readOrderChannel(value: unknown, record: SupplierAutoSosSupplierRecord): SupplierAutoOrderChannel {
  if (isOneOf(value, ['sms', 'phone', 'platform', 'manual'])) {
    return value;
  }

  return record.smsRecipient ? 'sms' : 'manual';
}

function readPaymentMode(value: unknown): SupplierAutoPaymentMode {
  return isOneOf(value, ['postpaid', 'bank_transfer', 'manual_online', 'platform_required', 'order_only'])
    ? value
    : 'postpaid';
}

function readConnectorStatus(value: unknown): SupplierAutoConnectorStatus {
  return isOneOf(value, ['manual', 'connector_planned', 'api_planned']) ? value : 'manual';
}

function readApprovalMode(value: unknown): SupplierAutoApprovalMode {
  return isOneOf(value, ['manager_required', 'manager_optional']) ? value : 'manager_required';
}

function readSettlementCycle(value: unknown): SupplierAutoSettlementCycle {
  return isOneOf(value, ['per_order', 'weekly', 'monthly', 'platform']) ? value : 'monthly';
}

function isOneOf<T extends string>(value: unknown, allowed: readonly T[]): value is T {
  return typeof value === 'string' && allowed.includes(value as T);
}

function readString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function readNumber(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  return fallback;
}

function updateDraft(
  state: SupplierAutoState,
  draftId: string,
  updater: (draft: SupplierAutoDraft, supplier: SupplierAutoSupplier) => SupplierAutoDraft,
): SupplierAutoState {
  return {
    ...state,
    activeDraftId: draftId,
    drafts: state.drafts.map((draft) => {
      if (draft.id !== draftId) {
        return draft;
      }

      const supplier = state.suppliers.find((candidate) => candidate.id === draft.supplierId);
      return supplier ? updater(draft, supplier) : draft;
    }),
  };
}

function finalizeDraft(draft: SupplierAutoDraft, supplier: SupplierAutoSupplier): SupplierAutoDraft {
  const totalAmount = calculateDraftTotal(draft.lines);
  const nextDraft = {
    ...draft,
    totalAmount,
  };

  return {
    ...nextDraft,
    phonePreview: buildPhonePreview(supplier, nextDraft),
  };
}

function calculateDraftTotal(lines: SupplierAutoDraftLine[]): number {
  return roundMoney(lines.reduce((sum, line) => (
    line.excluded ? sum : sum + line.orderQuantity * line.unitPrice
  ), 0));
}

function buildPhonePreview(supplier: SupplierAutoSupplier, draft: SupplierAutoDraft): string {
  const activeLines = draft.lines.filter((line) => !line.excluded && line.orderQuantity > 0);
  const lineText = activeLines
    .map((line) => `- ${line.materialName}: ${line.orderQuantity}${line.orderUnit}`)
    .join('\n');

  return [
    '[거래처 자동발주 Lab]',
    `거래처: ${supplier.name}`,
    `담당: ${supplier.ownerName || '-'}`,
    `주문채널: ${orderChannelLabels[supplier.orderChannel]}`,
    '',
    lineText || '- 선택된 품목 없음',
    '',
    `예상합계: ${formatSupplierAutoMoney(draft.totalAmount)}`,
    supplier.orderMemo ? `요청메모: ${supplier.orderMemo}` : '',
  ].filter(Boolean).join('\n');
}

function createDraftWarnings(supplier: SupplierAutoSupplier): string[] {
  const warnings: string[] = [];

  if (supplier.orderChannel === 'platform') {
    warnings.push('플랫폼 주문은 현재 시뮬레이션 상태입니다.');
  }

  if (supplier.paymentPolicy.mode === 'platform_required' || supplier.paymentPolicy.mode === 'manual_online') {
    warnings.push(`${paymentModeLabels[supplier.paymentPolicy.mode]} 정책은 결제 실행 전 승인 확인이 필요합니다.`);
  }

  if (supplier.paymentPolicy.connectorStatus !== 'manual') {
    warnings.push(`${connectorStatusLabels[supplier.paymentPolicy.connectorStatus]} 상태는 아직 실행 어댑터에 연결되지 않았습니다.`);
  }

  if (requiresExternalMarketProfile(supplier)) {
    const market = supplier.externalMarketProfile.marketName || '외부 마켓';
    warnings.push(`${market} 결제는 외부 페이지 장바구니와 관리자 승인 게이트가 필요합니다.`);
  }

  const blockingIssues = validateSupplierSetup(supplier).filter((issue) => issue.severity === 'error');
  blockingIssues.forEach((issue) => warnings.push(`${issue.title}: ${issue.detail}`));

  return warnings;
}

function createHistoryEntry(actor: string, action: string, detail: string, draftId?: string): SupplierAutoHistoryEntry {
  return {
    id: `history-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    at: new Date().toISOString(),
    actor,
    action,
    detail,
    draftId,
  };
}

function createSupplyItemFromMaterial(supplierId: string, material: SupplierAutoMaterial): SupplierAutoSupplyItem {
  return {
    id: `supply-${supplierId}-${material.id}`,
    materialId: material.id,
    supplierSku: material.name,
    orderUnit: material.orderUnit,
    packSize: 1,
    minOrderQuantity: 1,
    unitPrice: 0,
    leadTimeDays: 1,
    autoOrderEnabled: true,
    memo: '',
  };
}

function createDemoSuppliers(): SupplierAutoSupplier[] {
  return [
    {
      id: 'supplier-vision-food',
      name: '비전 아토푸드',
      ownerName: '김담당',
      smsRecipient: '010-1234-0001',
      businessProfile: {
        businessName: '비전 아토푸드',
        businessNumber: '123-45-67890',
        mainPhone: '031-000-1000',
        address: '경기 평택시 비전동',
        orderCutoffTime: '10:30',
        defaultDeliveryMemo: '오전 배송 가능 품목 우선',
      },
      status: 'active',
      orderChannel: 'sms',
      paymentPolicy: {
        mode: 'bank_transfer',
        connectorStatus: 'manual',
        approvalMode: 'manager_required',
        settlementPolicy: {
          settlementCycle: 'monthly',
          bankName: '국민은행',
          bankAccount: '000000-00-000000',
          accountHolder: '비전 아토푸드',
          taxInvoiceEmail: 'invoice@vision-food.example',
        },
        memo: '월말 정산 전 거래명세서 확인',
      },
      externalMarketProfile: {
        marketName: '',
        accountLabel: '',
        orderPageUrl: '',
        lastVerifiedAt: '',
        automationMemo: '',
      },
      orderMemo: '오전 배송 가능 품목 우선',
      supplyItems: [
        {
          id: 'supply-vision-chicken',
          materialId: 'material-chicken-leg',
          supplierSku: '닭다리살 5kg',
          orderUnit: 'kg',
          packSize: 5,
          minOrderQuantity: 5,
          unitPrice: 6500,
          leadTimeDays: 1,
          autoOrderEnabled: true,
          memo: '냉장',
        },
        {
          id: 'supply-vision-sauce',
          materialId: 'material-sauce-base',
          supplierSku: '소스 베이스 1box',
          orderUnit: 'box',
          packSize: 1,
          minOrderQuantity: 1,
          unitPrice: 19000,
          leadTimeDays: 2,
          autoOrderEnabled: true,
          memo: '',
        },
      ],
    },
    {
      id: 'supplier-owner-mart',
      name: '사장님마트',
      ownerName: '박매니저',
      smsRecipient: '010-1234-0002',
      businessProfile: {
        businessName: '사장님마트',
        businessNumber: '',
        mainPhone: '031-000-2000',
        address: '경기 평택시 소사동',
        orderCutoffTime: '14:00',
        defaultDeliveryMemo: '품절 시 대체품 제안 요청',
      },
      status: 'active',
      orderChannel: 'platform',
      paymentPolicy: {
        mode: 'platform_required',
        connectorStatus: 'connector_planned',
        approvalMode: 'manager_required',
        settlementPolicy: {
          settlementCycle: 'platform',
          bankName: '',
          bankAccount: '',
          accountHolder: '',
          taxInvoiceEmail: '',
        },
        memo: '플랫폼 결제 화면에서 최종 승인 필요',
      },
      externalMarketProfile: {
        marketName: '쿠팡',
        accountLabel: '사장님마트 구매계정',
        orderPageUrl: 'https://www.coupang.com/',
        lastVerifiedAt: '2026-05-24',
        automationMemo: '장바구니 담기 후 관리자 승인 필요',
      },
      orderMemo: '품절 시 대체품 제안 요청',
      supplyItems: [
        {
          id: 'supply-mart-cheese',
          materialId: 'material-mozzarella',
          supplierSku: '모짜렐라 슈레드 2kg',
          orderUnit: 'bag',
          packSize: 2,
          minOrderQuantity: 2,
          unitPrice: 14500,
          leadTimeDays: 1,
          autoOrderEnabled: true,
          memo: '냉동',
        },
        {
          id: 'supply-mart-pickle',
          materialId: 'material-pickle',
          supplierSku: '피클 슬라이스 3kg',
          orderUnit: 'box',
          packSize: 1,
          minOrderQuantity: 1,
          unitPrice: 9500,
          leadTimeDays: 1,
          autoOrderEnabled: true,
          memo: '',
        },
      ],
    },
    {
      id: 'supplier-packaging',
      name: '패키징하우스',
      ownerName: '이대표',
      smsRecipient: '010-1234-0003',
      businessProfile: {
        businessName: '패키징하우스',
        businessNumber: '',
        mainPhone: '02-000-3000',
        address: '서울 금천구',
        orderCutoffTime: '',
        defaultDeliveryMemo: '',
      },
      status: 'active',
      orderChannel: 'manual',
      paymentPolicy: {
        mode: 'postpaid',
        connectorStatus: 'manual',
        approvalMode: 'manager_optional',
        settlementPolicy: {
          settlementCycle: 'monthly',
          bankName: '',
          bankAccount: '',
          accountHolder: '',
          taxInvoiceEmail: '',
        },
        memo: '월 1회 세금계산서',
      },
      externalMarketProfile: {
        marketName: '',
        accountLabel: '',
        orderPageUrl: '',
        lastVerifiedAt: '',
        automationMemo: '',
      },
      orderMemo: '',
      supplyItems: [
        {
          id: 'supply-pack-box',
          materialId: 'material-paper-box',
          supplierSku: '포장 박스 100ea',
          orderUnit: 'bundle',
          packSize: 1,
          minOrderQuantity: 1,
          unitPrice: 32000,
          leadTimeDays: 3,
          autoOrderEnabled: true,
          memo: '',
        },
      ],
    },
  ];
}

function createBlankSupplier(index: number): SupplierAutoSupplier {
  const id = `supplier-new-${Date.now()}`;

  return {
    id,
    name: `새 거래처 ${index}`,
    ownerName: '',
    smsRecipient: '',
    businessProfile: {
      businessName: '',
      businessNumber: '',
      mainPhone: '',
      address: '',
      orderCutoffTime: '',
      defaultDeliveryMemo: '',
    },
    status: 'active',
    orderChannel: 'sms',
    paymentPolicy: {
      mode: 'bank_transfer',
      connectorStatus: 'manual',
      approvalMode: 'manager_required',
      settlementPolicy: {
        settlementCycle: 'monthly',
        bankName: '',
        bankAccount: '',
        accountHolder: '',
        taxInvoiceEmail: '',
      },
      memo: '',
    },
    externalMarketProfile: {
      marketName: '',
      accountLabel: '',
      orderPageUrl: '',
      lastVerifiedAt: '',
      automationMemo: '',
    },
    orderMemo: '',
    supplyItems: [],
  };
}

function requiresExternalMarketProfile(supplier: SupplierAutoSupplier): boolean {
  return supplier.orderChannel === 'platform'
    || supplier.paymentPolicy.mode === 'platform_required'
    || supplier.paymentPolicy.mode === 'manual_online';
}

function cloneSuppliers(suppliers: SupplierAutoSupplier[]): SupplierAutoSupplier[] {
  return suppliers.map((supplier) => ({
    ...supplier,
    businessProfile: { ...supplier.businessProfile },
    paymentPolicy: {
      ...supplier.paymentPolicy,
      settlementPolicy: { ...supplier.paymentPolicy.settlementPolicy },
    },
    externalMarketProfile: { ...supplier.externalMarketProfile },
    supplyItems: supplier.supplyItems.map((item) => ({ ...item })),
  }));
}

function cloneMaterials(materials: SupplierAutoMaterial[]): SupplierAutoMaterial[] {
  return materials.map((material) => ({ ...material }));
}

function createDemoMaterials(): SupplierAutoMaterial[] {
  return [
    {
      id: 'material-chicken-leg',
      name: '닭다리살',
      category: '육류',
      locationName: '강남점',
      currentStock: 8,
      minStock: 12,
      targetStock: 30,
      comparisonUnit: 'kg',
      orderUnit: 'kg',
      lastInboundAt: '2026-05-23',
    },
    {
      id: 'material-mozzarella',
      name: '모짜렐라 치즈',
      category: '유제품',
      locationName: '강남점',
      currentStock: 1.5,
      minStock: 4,
      targetStock: 10,
      comparisonUnit: 'kg',
      orderUnit: 'bag',
      lastInboundAt: '2026-05-21',
    },
    {
      id: 'material-paper-box',
      name: '포장 박스',
      category: '소모품',
      locationName: '강남점',
      currentStock: 120,
      minStock: 100,
      targetStock: 240,
      comparisonUnit: 'ea',
      orderUnit: 'bundle',
      lastInboundAt: '2026-05-20',
    },
    {
      id: 'material-sauce-base',
      name: '소스 베이스',
      category: '소스',
      locationName: '강남점',
      currentStock: 3,
      minStock: 6,
      targetStock: 15,
      comparisonUnit: 'box',
      orderUnit: 'box',
      lastInboundAt: '2026-05-18',
    },
    {
      id: 'material-pickle',
      name: '피클',
      category: '채소/절임',
      locationName: '강남점',
      currentStock: 0,
      minStock: 2,
      targetStock: 8,
      comparisonUnit: 'box',
      orderUnit: 'box',
      lastInboundAt: '2026-05-15',
    },
  ];
}

function roundUpToPack(quantity: number, packSize: number): number {
  const safePackSize = Number.isFinite(packSize) && packSize > 0 ? packSize : 1;
  return Math.ceil(quantity / safePackSize) * safePackSize;
}

function roundQuantity(quantity: number): number {
  return Math.round(quantity * 100) / 100;
}

function roundMoney(value: number): number {
  return Math.round(value);
}
